import { useCallback, useEffect, useState } from "react";

import type { SpeedScreenFormLayout } from "@framework/provider-settings";

export const SPEED_SCREEN_FORMS = ["orbit", "nyquist", "bode", "cascade"] as const;
export type SpeedScreenForm = (typeof SPEED_SCREEN_FORMS)[number];
export const SPEED_SCREEN_FORM_DRAG_TYPE = "application/x-sp-speed-screen-form";

interface ISpeedWindow {
  form: SpeedScreenForm | null;
  collapsed: boolean;
}

interface ISpeedWindowsState {
  windows: ISpeedWindow[];
  activeWindowId: number | null;
  expandedWindowId: number | null;
}

const createWindowsState = (count: number): ISpeedWindowsState => ({
  windows: Array.from({ length: count }, () => ({ form: null, collapsed: false })),
  activeWindowId: null,
  expandedWindowId: null,
});

export const useSpeedScreenForms = (layout: SpeedScreenFormLayout, trainId: number | null) => {
  const windowCount = layout === "1" ? 1 : layout === "4" ? 4 : 2;
  const [state, setState] = useState(() => createWindowsState(windowCount));

  useEffect(() => {
    setState(previous => createWindowsState(previous.windows.length));
  }, [trainId]);

  useEffect(() => {
    setState(previous => ({
      windows: Array.from({ length: windowCount }, (_, index) => ({
        form: previous.windows[index]?.form ?? null,
        collapsed: false,
      })),
      activeWindowId:
        previous.activeWindowId !== null && previous.activeWindowId < windowCount ? previous.activeWindowId : null,
      expandedWindowId: null,
    }));
  }, [layout, windowCount]);

  const toggleForm = useCallback((form: SpeedScreenForm) => {
    setState(previous => {
      const selectedIndex = previous.windows.findIndex(window => window.form === form);
      const index = selectedIndex >= 0 ? selectedIndex : previous.windows.findIndex(window => window.form === null);
      if (index < 0) return previous;

      return {
        windows: previous.windows.map((window, windowId) =>
          windowId === index ? { form: selectedIndex >= 0 ? null : form, collapsed: false } : window,
        ),
        activeWindowId:
          selectedIndex >= 0 ? (previous.activeWindowId === index ? null : previous.activeWindowId) : index,
        expandedWindowId: null,
      };
    });
  }, []);

  const placeForm = useCallback((form: SpeedScreenForm, windowId: number) => {
    setState(previous => {
      if (
        !previous.windows[windowId] ||
        previous.windows[windowId].form !== null ||
        previous.windows.some(window => window.form === form)
      ) {
        return previous;
      }

      return {
        windows: previous.windows.map((window, index) => (index === windowId ? { form, collapsed: false } : window)),
        activeWindowId: windowId,
        expandedWindowId: null,
      };
    });
  }, []);

  const selectWindow = useCallback((windowId: number) => {
    setState(previous => ({ ...previous, activeWindowId: windowId }));
  }, []);

  const toggleExpand = useCallback((windowId: number) => {
    setState(previous => ({
      ...previous,
      windows: previous.windows.map((window, index) => (index === windowId ? { ...window, collapsed: false } : window)),
      activeWindowId: windowId,
      expandedWindowId: previous.expandedWindowId === windowId ? null : windowId,
    }));
  }, []);

  const toggleCollapse = useCallback((windowId: number) => {
    setState(previous => ({
      ...previous,
      windows: previous.windows.map((window, index) =>
        index === windowId ? { ...window, collapsed: !window.collapsed } : window,
      ),
      activeWindowId: windowId,
      expandedWindowId: null,
    }));
  }, []);

  return { ...state, toggleForm, placeForm, selectWindow, toggleExpand, toggleCollapse };
};
